/** Payment method marks for the landing money section — official PNGs for wallets, simple-icons elsewhere. */

import Image from "next/image"
import {
  siAmericanexpress,
  siVisa,
  type SimpleIcon,
} from "simple-icons"

type LogoProps = { className?: string; variant?: "default" | "tile" }

const BRAND_ICONS: Record<"visa" | "amex", SimpleIcon> = {
  visa: siVisa,
  amex: siAmericanexpress,
}

/** Fill colors tuned for dark glass tiles — white marks where the brand allows it. */
const ON_DARK_FILL: Record<"visa" | "amex", string> = {
  visa: "#FFFFFF",
  amex: "#2E77BC",
}

/** Per-mark display height — each asset has a different aspect ratio. */
const LOGO_HEIGHT: Record<PaymentMethodKey, { default: number; tile: number }> = {
  visa: { default: 20, tile: 24 },
  mastercard: { default: 28, tile: 22 },
  amex: { default: 24, tile: 28 },
  apple_pay: { default: 18, tile: 28 },
  google_pay: { default: 20, tile: 28 },
  bank: { default: 20, tile: 22 },
}

const PNG_LOGOS: Record<"apple_pay" | "google_pay", { src: string; alt: string; width: number; height: number }> = {
  apple_pay: { src: "/images/payment-logos/apple-pay.png", alt: "Apple Pay", width: 360, height: 240 },
  google_pay: { src: "/images/payment-logos/google-pay.png", alt: "Google Pay", width: 480, height: 270 },
}

function BrandMark({
  icon,
  fill,
  height,
  className,
}: {
  icon: SimpleIcon
  fill: string
  height: number
  className?: string
}) {
  return (
    <svg
      role="img"
      aria-label={icon.title}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className={className ?? "shrink-0"}
      style={{ height, width: "auto", maxWidth: "100%" }}
    >
      <path d={icon.path} fill={fill} />
    </svg>
  )
}

function MastercardMark({
  height,
  className,
  compact = false,
}: {
  height: number
  className?: string
  compact?: boolean
}) {
  const r = compact ? 6.5 : 8
  return (
    <svg
      role="img"
      aria-label="Mastercard"
      viewBox="0 0 32 20"
      xmlns="http://www.w3.org/2000/svg"
      className={className ?? "shrink-0"}
      style={{ height, width: "auto", maxWidth: "100%" }}
    >
      <circle cx="12" cy="10" r={r} fill="#EB001B" />
      <circle cx="20" cy="10" r={r} fill="#F79E1B" />
    </svg>
  )
}

export function BankLogo({ className = "h-[18px] w-[18px]" }: LogoProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      style={{ color: "var(--pg-muted)" }}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M5 10V19M9 10V19M15 10V19M19 10V19M4 19h16M6 6l6-3 6 3" />
    </svg>
  )
}

export type PaymentMethodKey = "visa" | "mastercard" | "amex" | "apple_pay" | "google_pay" | "bank"

export function PaymentMethodLogo({
  kind,
  className,
  variant = "default",
}: {
  kind: PaymentMethodKey
  className?: string
  variant?: "default" | "tile"
}) {
  const h = LOGO_HEIGHT[kind][variant]

  if (kind === "bank") {
    return <BankLogo className={className ?? `h-[${h}px] w-[${h}px]`} />
  }

  if (kind === "apple_pay" || kind === "google_pay") {
    const asset = PNG_LOGOS[kind]
    return (
      <Image
        src={asset.src}
        alt={asset.alt}
        width={asset.width}
        height={asset.height}
        className={className ?? "w-auto max-w-[92%] object-contain"}
        style={{ height: h }}
        draggable={false}
      />
    )
  }

  if (kind === "mastercard") {
    return <MastercardMark height={h} className={className} compact={variant === "tile"} />
  }

  const icon = BRAND_ICONS[kind]
  return <BrandMark icon={icon} fill={ON_DARK_FILL[kind]} height={h} className={className} />
}
